import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Group,
  Stack,
  Text,
  Box,
  Button,
  Modal,
  TextInput,
  Divider,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { DeletePei, LoadPeis, SavePei} from "../../actions/peiActions";
import PeiCardButton from "./PeiCardButton";
import ModalDialog from "../ModalDialog";
import APP_STATES from "../../enums/AppStates";

const PlanesEstrategicos = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { data: plans, loading, saving, deletingId } = useSelector((state) => state.pei);

  const [modalOpen, setModalOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [peiCode, setPeiCode] = useState("");

  useEffect(() => {
    dispatch(LoadPeis(null, null, user));
  }, [dispatch, user]);

  useMemo(() => plans.find((p) => String(p.status).toLowerCase() === "active"), [plans]);

  const navigatePeiManagement = (pei) => navigate(`/admin/pei/${pei.Id}/manage`);

  const handleOpenModal = () => {
    setPeiCode("");
    setModalOpen(true);
  };

  const handleClosePEIModal = () => {
    if (saving) return;
    setModalOpen(false);
    setPeiCode("");
  };

  const handleCreatePlan = async () => {
    const code = String(peiCode || "").trim();
    if (!code) return;

    const payload = {
      Codigo: code,
      estado: { Id: APP_STATES.BORRADOR},
    };

    const created = await dispatch(SavePei(payload, user));
    if (created) {
      setModalOpen(false);
      setPeiCode("");
    }
  };

  const handleOpenDeleteDialog = (plan) => {
    const isDraft = plan?.estado.Id.toUpperCase() === APP_STATES.BORRADOR;
    if (!isDraft) return;

    const data = {
      title: "Confirmar eliminación",
      description: `¿Estás seguro que deseas eliminar el plan estratégico ${plan.Codigo}? Esta acción no se puede deshacer.`,
      confirmColor: "red",
      onConfirm: () => {handleDeletePlan(plan.Id); setDialogOpen(false);},
    };

    setDialogData(data);
    setDialogOpen(true);
  };

  const handleDeletePlan = (planId) => {
    dispatch(DeletePei(planId, user));
  };

  return (
    <Box>
      <Card shadow='sm' p="lg" radius={'md'}>
        <Group justify="space-between" mb="md">
          <Text size='xl' fw={600}>Gestión de Planes Estratégicos</Text>

          <Button
            color="#e72166"
            leftSection={<IconPlus size={16} />}
            onClick={handleOpenModal}
          >
            Nuevo Plan
          </Button>
        </Group>

        <Stack gap="sm" mt="sm">
          {plans?.length > 0 ? (
            <>
              {plans
                .filter((plan) => plan.estado.Id.toUpperCase() === APP_STATES.ACTIVO)
                .map((plan) => (
                  <PeiCardButton
                    key={plan.id || plan.code}
                    pei={plan}
                    onOpen={() => navigatePeiManagement(plan)}
                    onDelete={() => handleDeletePlan(plan)}
                    deleting={deletingId === (plan.id || plan.code)}
                  />
                ))
              }
              <Divider my={'md'}/>

              {plans
                .filter((plan) => plan.estado.Id.toUpperCase() !== APP_STATES.ACTIVO)
                .sort((a, b) => new Date(b.dt_lu) - new Date(a.dt_lu))
                .map((plan) => (
                  <PeiCardButton
                    key={plan.id || plan.code}
                    pei={plan}
                    onOpen={() => navigatePeiManagement(plan)}
                    deleting={deletingId === (plan.id || plan.code)}
                  />
                ))
              }
            </>
          ) : (
            <Text c="dimmed" ta={"center"}>
              No hay planes disponibles para mostrar
            </Text>
          )}
        </Stack>
      </Card>

      <Modal
        opened={modalOpen}
        onClose={handleClosePEIModal}
        closeOnClickOutside={false}
        centered
        size="md"
        radius="md"
        overlayProps={{ blur: 1.5 }}
        title={<Text fw={600}>Nuevo Plan Estratégico</Text>}
      >
        <Stack gap="sm">
          <Text>
            Ingresá el código del PEI (ej.: <i>PEI-2026-2029</i>). Se creará en estado
            <b> Borrador</b>.
          </Text>
          <TextInput
            label="Código del Plan"
            placeholder="PEI-20XX-20XX"
            value={peiCode}
            onChange={(e) => setPeiCode(e.currentTarget.value)}
            withAsterisk
          />
          <Group justify="space-between" mt="xl">
            <Button variant="default" onClick={handleClosePEIModal} disabled={saving}>
              Cancelar
            </Button>
            <Button color="#e72166" onClick={handleCreatePlan} loading={saving}>
              Crear Plan
            </Button>
          </Group>
        </Stack>
      </Modal>
      <ModalDialog
        opened={dialogOpen}
        onClose = {() => setDialogOpen(false)}
        onConfirm = {() => dialogData?.onConfirm && dialogData.onConfirm()}
        title = {dialogData?.title}
        description = {dialogData?.description}
        confirmColor = {dialogData?.confirmColor}
      />
    </Box>
  );
};

export default PlanesEstrategicos;
